import { Badge, HStack } from '@chakra-ui/react';
import type { DisplayRightsProposalSummary } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import {
  DISPLAY_RIGHTS_META_TONE,
  DISPLAY_RIGHTS_TONE,
  resolveDisplayRightsStatus,
  type DisplayRightsMetaState,
} from './display-rights-status';

interface DisplayRightsProposalBadgeProps {
  proposal: DisplayRightsProposalSummary | null;
  size?: 'xs' | 'sm';
}

/**
 * The contract pill: where the display-rights negotiation stands. Null reads as
 * "no terms" rather than disappearing, so an authorization never looks like it
 * silently carries an agreement.
 *
 * The label uses the DISPLAYED status, so a version whose acceptance window has
 * passed reads "expired" even while the server still stores it as sent.
 */
export function DisplayRightsProposalBadge({
  proposal,
  size = 'sm',
}: DisplayRightsProposalBadgeProps) {
  const { t } = useTranslation();

  if (!proposal) {
    return (
      <Badge
        colorPalette="gray"
        variant="subtle"
        size={size}
        textTransform="none"
        data-testid="display-rights-proposal-badge"
        data-status="none"
      >
        {t('displayRights.status.none')}
      </Badge>
    );
  }

  const status = resolveDisplayRightsStatus(proposal);
  const label = t(`displayRights.status.${status}`);

  return (
    <Badge
      colorPalette={DISPLAY_RIGHTS_TONE[status]}
      variant="subtle"
      size={size}
      textTransform="none"
      data-testid="display-rights-proposal-badge"
      data-status={status}
    >
      {proposal.versionNumber > 1
        ? t('displayRights.badge.withVersion', { label, version: proposal.versionNumber })
        : label}
    </Badge>
  );
}

interface DisplayRightsMetaBadgeProps {
  state: DisplayRightsMetaState;
  size?: 'xs' | 'sm';
}

/** The Instagram pill: where the Meta authorization stands, independently. */
export function DisplayRightsMetaBadge({ state, size = 'sm' }: DisplayRightsMetaBadgeProps) {
  const { t } = useTranslation();

  return (
    <Badge
      colorPalette={DISPLAY_RIGHTS_META_TONE[state]}
      variant="subtle"
      size={size}
      textTransform="none"
      data-testid="display-rights-meta-badge"
      data-state={state}
    >
      {t(`displayRights.meta.${state}`)}
    </Badge>
  );
}

interface DisplayRightsStatusBadgesProps {
  proposal: DisplayRightsProposalSummary | null;
  /** Omit to show the contract pill alone, where Meta has its own column. */
  metaState?: DisplayRightsMetaState;
  size?: 'xs' | 'sm';
}

/**
 * The crossed state, shown as one unit everywhere an authorization appears.
 *
 * The two dimensions are independent — terms can be agreed while Instagram is
 * still deciding, and the reverse — so they are two pills side by side, never
 * merged into a single verdict.
 */
export function DisplayRightsStatusBadges({
  proposal,
  metaState,
  size = 'sm',
}: DisplayRightsStatusBadgesProps) {
  return (
    <HStack gap="1.5" flexWrap="wrap" data-testid="display-rights-status-badges">
      <DisplayRightsProposalBadge proposal={proposal} size={size} />
      {metaState && <DisplayRightsMetaBadge state={metaState} size={size} />}
    </HStack>
  );
}
